import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Snackbar
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { Refresh, Visibility, Search, Error as ErrorIcon, Add } from '@material-ui/icons';
import ReactJson from 'react-json-view';
import { useMezzanine } from '../../context/MezzanineContext';
import CreateProgramGroupForm from './CreateProgramGroupForm';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
  },
  searchBox: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  tableCell: {
    padding: '10px 16px',
    fontSize: '12px',
  },
  audioBox: {
    marginBottom: '6px',
    display: 'flex',
    alignItems: 'center',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  langBadge: {
    padding: '2px 8px',
    borderRadius: '4px',
    fontWeight: 'bold',
    display: 'inline-block',
    minWidth: '40px',
    textAlign: 'center',
    marginRight: '8px',
    fontSize: '11px',
  },
  channelMode: {
    fontSize: '11px',
    color: '#555',
  },
  pidBadge: {
    fontSize: '10px',
    color: '#888',
    marginLeft: '4px',
  },
  videoPid: {
    fontSize: '10px',
    color: '#795548',
    fontWeight: 'normal',
  },
  vancPid: {
    fontSize: '10px',
    color: '#7b1fa2',
    fontWeight: 'bold',
  },
}));

// Language color mapping - soft pastel colors
const getLanguageColor = (lang) => {
  const colors = {
    'eng': { bg: '#e8f5e9', text: '#2e7d32' },  // Light green
    'hin': { bg: '#e1f5fe', text: '#0277bd' },  // Light sky blue (changed from orange)
    'spa': { bg: '#ffebee', text: '#c62828' },  // Light red
    'fra': { bg: '#e3f2fd', text: '#1565c0' },  // Light blue
    'deu': { bg: '#f3e5f5', text: '#7b1fa2' },  // Light purple
    'ita': { bg: '#e0f7fa', text: '#00838f' },  // Light teal
    'por': { bg: '#ede7f6', text: '#4527a0' },  // Light deep purple
    'jpn': { bg: '#fce4ec', text: '#c2185b' },  // Light pink (changed from deep orange)
    'kor': { bg: '#e0f2f1', text: '#00695c' },  // Light dark teal
    'zho': { bg: '#f3e5f5', text: '#6a1b9a' },  // Light purple (changed from brown)
    'ara': { bg: '#efebe9', text: '#5d4037' },  // Light brown
    'rus': { bg: '#eceff1', text: '#37474f' },  // Light blue grey
  };
  return colors[lang?.toLowerCase()] || { bg: '#f5f5f5', text: '#616161' };  // Default light gray
};

const ProgramGroups = () => {
  const classes = useStyles();
  const { programGroups, programGroupsLoading, programGroupsError, fetchProgramGroups } = useMezzanine();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewModal, setViewModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [createFormOpen, setCreateFormOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Helper functions
  const getVideoDisplay = (group) => {
    const label = group?.video?.profile_label || 'N/A';
    const pid = group?.video?.pid;
    return (
      <span>
        {label}
        {pid && <span className={classes.videoPid}> [{pid}]</span>}
      </span>
    );
  };

  const getColorProfile = (group) => group?.color?.profile_label || 'N/A';

  const getVancDisplay = (group) => {
    if (!group?.vancs || group.vancs.length === 0) return 'N/A';
    const vanc = group.vancs[0];
    return (
      <span>
        {vanc?.profile_label || 'N/A'}
        {vanc?.pid && <span className={classes.vancPid}> [{vanc.pid}]</span>}
      </span>
    );
  };

  const getAudios = (group) => {
    if (!group?.audios || group.audios.length === 0) return 'N/A';
    return group.audios.map((audio, idx) => {
      const langColor = getLanguageColor(audio.language);
      return (
        <Box key={idx} className={classes.audioBox}>
          <span 
            className={classes.langBadge}
            style={{ backgroundColor: langColor.bg, color: langColor.text }}
          >
            {audio.language?.toUpperCase() || 'N/A'}
          </span>
          <span className={classes.channelMode}>
            {audio.channel_mode || 'N/A'}
            {audio.pid && <span className={classes.pidBadge}>[{audio.pid}]</span>}
          </span>
        </Box>
      );
    });
  };

  const handleView = (group) => {
    setSelectedGroup(group);
    setViewModal(true);
  };

  // Filter data based on search query
  const filteredData = useMemo(() => {
    if (!searchQuery) return programGroups;
    const query = searchQuery.toLowerCase();
    return programGroups.filter(group => {
      // Search in group name
      if (group.group_name?.toLowerCase().includes(query)) return true;
      // Search in video profile
      if (group.video?.profile_label?.toLowerCase().includes(query)) return true;
      // Search in color profile
      if (group.color?.profile_label?.toLowerCase().includes(query)) return true;
      // Search in audio languages and modes
      if (group.audios?.some(a => 
        a.language?.toLowerCase().includes(query) ||
        a.channel_mode?.toLowerCase().includes(query) ||
        a.profile_label?.toLowerCase().includes(query)
      )) return true;
      // Search in vanc profile
      if (group.vancs?.some(v => v.profile_label?.toLowerCase().includes(query))) return true;
      return false;
    });
  }, [programGroups, searchQuery]);

  // Loading state
  if (programGroupsLoading && programGroups.length === 0) {
    return (
      <Box className={classes.container} display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress style={{ color: '#ff6f00' }} />
      </Box>
    );
  }

  // Error state
  if (programGroupsError && programGroups.length === 0) {
    return (
      <Box className={classes.container}>
        <Paper style={{ padding: '20px', backgroundColor: '#ffebee', textAlign: 'center' }}>
          <ErrorIcon style={{ color: '#c62828', fontSize: '48px' }} />
          <Typography variant="h6" style={{ color: '#c62828', marginTop: '10px' }}>
            Failed to load program groups
          </Typography>
          <Typography variant="body2" style={{ color: '#c62828', marginBottom: '15px' }}>
            {programGroupsError}
          </Typography>
          <IconButton onClick={fetchProgramGroups} style={{ color: '#c62828', border: '2px solid #c62828' }}>
            <Refresh />
          </IconButton>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      {/* Search and Refresh */}
      <Box className={classes.searchBox}>
        <Box display="flex" alignItems="center" style={{ gap: '12px' }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateFormOpen(true)}
            style={{ backgroundColor: '#ff6f00', color: 'white' }}
          >
            Create
          </Button>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search by name, video, audio, color, vanc..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: '400px', backgroundColor: 'white' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search style={{ color: '#ff6f00' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <IconButton
          onClick={fetchProgramGroups}
          disabled={programGroupsLoading}
          size="small"
          style={{ color: '#ff6f00', border: '1px solid #ff6f00', borderRadius: '4px', padding: '6px' }}
        >
          {programGroupsLoading ? <CircularProgress size={20} style={{ color: '#ff6f00' }} /> : <Refresh fontSize="small" />}
        </IconButton>
      </Box>

      {/* Create Program Group Form */}
      <CreateProgramGroupForm
        open={createFormOpen}
        onClose={() => setCreateFormOpen(false)}
        onSuccess={fetchProgramGroups}
        onNotify={({ message, severity }) => setSnackbar({ open: true, message, severity })}
      />

      {/* Table */}
      <TableContainer component={Paper} style={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Table size="small">
          <TableHead>
            <TableRow style={{ backgroundColor: '#e0e0e0' }}>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Video</strong></TableCell>
              <TableCell><strong>Audios</strong></TableCell>
              <TableCell><strong>Color</strong></TableCell>
              <TableCell><strong>VANC</strong></TableCell>
              <TableCell align="center"><strong>View</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((group, index) => (
              <TableRow 
                key={group.group_name || index} 
                hover
                style={{ 
                  backgroundColor: '#fff8f0',
                  borderBottom: '8px solid #fff'
                }}
              >
                <TableCell className={classes.tableCell} style={{ fontWeight: 'bold' }}>
                  {group.group_name || 'N/A'}
                </TableCell>
                <TableCell className={classes.tableCell}>{getVideoDisplay(group)}</TableCell>
                <TableCell className={classes.tableCell}>{getAudios(group)}</TableCell>
                <TableCell className={classes.tableCell}>{getColorProfile(group)}</TableCell>
                <TableCell className={classes.tableCell}>{getVancDisplay(group)}</TableCell>
                <TableCell className={classes.tableCell} align="center">
                  <IconButton size="small" onClick={() => handleView(group)} style={{ color: '#ff6f00' }}>
                    <Visibility fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Modal */}
      <Dialog
        open={viewModal}
        onClose={() => setViewModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { height: '80vh' } }}
      >
        <DialogTitle style={{ borderBottom: '1px solid #eee' }}>
          Program Group Details - {selectedGroup?.group_name}
        </DialogTitle>
        <DialogContent style={{ display: 'flex', flexDirection: 'column', padding: '20px', overflow: 'hidden' }}>
          <Box style={{ backgroundColor: '#f5f5f5', padding: '16px', borderRadius: '4px', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '4px', fontSize: '12px', overflowY: 'auto', flex: 1 }}>
              {selectedGroup && (
                <ReactJson
                  src={selectedGroup}
                  name="program_group"
                  collapsed={1}
                  onEdit={false}
                  onAdd={false}
                  onDelete={false}
                  displayDataTypes={false}
                  displayObjectSize={false}
                  theme="bright:inverted"
                  style={{ fontSize: '12px' }}
                />
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions style={{ borderTop: '1px solid #eee', padding: '12px 20px' }}>
          <Button onClick={() => setViewModal(false)} style={{ color: '#ff6f00' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProgramGroups;
